/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Checkbox, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { accessControlService } from '../AccessControlService';
import { PermissionState, PortalPermission } from '../Model/AccessControlModel';
import { Loader } from '../../../../Common/Components/Loader';
import { DragableUI } from './BaseDragable';

interface Props {
  groupname: string;
  setselectedGroupPermissions: React.Dispatch<
    React.SetStateAction<PortalPermission[]>
  >;
}

const GroupPermissionsDragable = ({
  groupname,
  setselectedGroupPermissions,
}: Props) => {
  const [loading, setLoading] = useState(true);
  const [listState, setListState] = useState<PermissionState>({
    unselectedlist: [],
    selectedlist: [],
  });
  const [partnerGroupDefaultPermissions, setPartnerGroupDefaultPermissions] =
    useState<PortalPermission[]>([]);
  const [applyPartnerGroupPermissions, setApplyPartnerGroupPermissions] =
    useState(false);

  useEffect(() => {
    loadPartnerGroupDefaultPermissions().catch((err) => console.log(err));
    if (groupname === '') {
      loadAvailablePermissions().catch((err) => console.log(err));
    } else {
      loadGroupAndAvailablePermissions().catch((err) => console.log(err));
    }
  }, [groupname]);

  const loadPartnerGroupDefaultPermissions = async () => {
    const partnerGroupPermissions =
      await accessControlService.GetPartnerGroupDefaultPermissions();
    setPartnerGroupDefaultPermissions(partnerGroupPermissions.Items);
  };

  const loadGroupAndAvailablePermissions = async () => {
    setLoading(true);
    const groupAndAvailablePermissions =
      await accessControlService.getGroupAndAvailablePermissions(groupname);

    setListState({
      unselectedlist: groupAndAvailablePermissions.AvailablePermissions,
      selectedlist: groupAndAvailablePermissions.GroupPermissions,
    });
    setselectedGroupPermissions(groupAndAvailablePermissions.GroupPermissions);
    setLoading(false);
  };

  const handleAddPartnerGroupDefaultPermissions = (checked: boolean) => {
    setApplyPartnerGroupPermissions(checked);

    setListState((prevState) => {
      if (checked) {
        return {
          unselectedlist: prevState.unselectedlist.filter(
            (item) =>
              !partnerGroupDefaultPermissions.some(
                (p) => p.PermissionName === item.PermissionName,
              ),
          ),
          selectedlist: [
            ...prevState.selectedlist,
            ...partnerGroupDefaultPermissions.filter(
              (p) =>
                !prevState.selectedlist.some(
                  (s) => s.PermissionName === p.PermissionName,
                ),
            ),
          ],
        };
      } else {
        return {
          unselectedlist: [
            ...prevState.unselectedlist,
            ...partnerGroupDefaultPermissions.filter(
              (p) =>
                !prevState.unselectedlist.some(
                  (u) => u.PermissionName === p.PermissionName,
                ),
            ),
          ],
          selectedlist: prevState.selectedlist.filter(
            (item) =>
              !partnerGroupDefaultPermissions.some(
                (p) => p.PermissionName === item.PermissionName,
              ),
          ),
        };
      }
    });

    setselectedGroupPermissions((prev) => {
      if (checked) {
        return [
          ...prev,
          ...partnerGroupDefaultPermissions.filter(
            (p) =>
              !prev.some((item) => item.PermissionName === p.PermissionName),
          ),
        ];
      } else {
        return prev.filter(
          (item) =>
            !partnerGroupDefaultPermissions.some(
              (p) => p.PermissionName === item.PermissionName,
            ),
        );
      }
    });
  };

  const loadAvailablePermissions = async () => {
    setLoading(true);
    const groupAndAvailablePermissions =
      await accessControlService.getAvailablePermissions();

    setListState({
      unselectedlist: groupAndAvailablePermissions.AvailablePermissions,
      selectedlist: groupAndAvailablePermissions.GroupPermissions,
    });
    setLoading(false);
  };

  const parseItemId = (id: string) => {
    const lastDash = id.lastIndexOf('-');
    return {
      listId: id.substring(0, lastDash),
      index: parseInt(id.substring(lastDash + 1), 10),
    };
  };

  const onDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const { listId: startList, index: startIndex } = parseItemId(
      String(active.id),
    );
    const { listId: finishList, index: finishIndex } = parseItemId(
      String(over.id),
    );

    if (startList === finishList) {
      setListState((prevState) => {
        const newList = [...prevState[startList]];
        const [removed] = newList.splice(startIndex, 1);
        newList.splice(finishIndex, 0, removed);
        return { ...prevState, [startList]: newList };
      });
    } else {
      setListState((prevState) => {
        const sourceItems = [...prevState[startList]];
        const destItems = [...prevState[finishList]];
        const [removed] = sourceItems.splice(startIndex, 1);
        destItems.splice(finishIndex, 0, removed);

        const updatedState: PermissionState = {
          ...prevState,
          [startList]: sourceItems,
          [finishList]: destItems,
        };

        setselectedGroupPermissions(updatedState.selectedlist);
        return updatedState;
      });
    }
  };

  return (
    <>
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <>
          <Box
            sx={{
              padding: 1,
              display: 'flex',
              alignContent: 'center',
              alignItems: 'center',
            }}
          >
            <Checkbox
              id={'partnerGroupPermission'}
              checked={applyPartnerGroupPermissions}
              onChange={(e) =>
                handleAddPartnerGroupDefaultPermissions(e.target.checked)
              }
            />
            <Typography variant="body2">Partner Group Permissions</Typography>
          </Box>
          <DragableUI
            onDragEnd={onDragEnd}
            liststate={listState}
            sourceList="Available Permissions"
            destListTitle="Selected Permissions"
          />
        </>
      )}
    </>
  );
};

export default GroupPermissionsDragable;
